/**
 * Remembers how the user signed in, so it can be attached to `posthog.identify`
 * as a person property and every event becomes sliceable by auth channel.
 *
 * This needs persisting rather than passing around because Okta and Entra hand
 * the browser over to the identity provider: the app is torn down and rebuilt
 * between the user picking a channel and `identify` running.
 *
 * Written when the user commits to a channel, not when the login succeeds. That
 * is safe because the value is only ever read while authenticated, and the login
 * screens are unauthenticated-only — so a failed attempt can never be the value
 * that a later `identify` reads.
 */
import { DurableStorageKeys, localStore } from '_clean-code/utils/storage';

export enum EAuthChannel {
  Email = 'email',
  Okta = 'okta',
  Entra = 'entra'
}

/**
 * Part of the analytics identity: it survives the auth gate's storage reset
 * alongside PostHog's own state, and is disposed of by a real logout.
 */
const AUTH_CHANNEL_KEY = DurableStorageKeys.AUTH_CHANNEL;

const isAuthChannel = (value: string | null): value is EAuthChannel =>
  Object.values(EAuthChannel).includes(value as EAuthChannel);

export function setAuthChannel(channel: EAuthChannel): void {
  // Storage is unavailable in some privacy modes, and analytics must never be
  // the reason a login fails — `localStore` swallows and logs that case.
  localStore.write(AUTH_CHANNEL_KEY, channel);
}

/** Null for sessions that predate this tracking. */
export function getAuthChannel(): EAuthChannel | null {
  const stored = localStore.read(AUTH_CHANNEL_KEY);
  return isAuthChannel(stored) ? stored : null;
}
